import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Briefcase,
  Building,
  Clock,
  MapPin,
  Users,
  GraduationCap,
  Award,
  FileText,
  Search,
  X,
  ChevronDown,
  Calendar
} from 'lucide-react';

const JobInfoSection = ({ 
  formData, 
  errors, 
  onInputChange,
  onArrayFieldChange
}) => {
  const [searchTerms, setSearchTerms] = useState({
    city: '',
    skills: '',
    education: '',
    organization: '',
    program: '',
    industry: ''
  });

  const [apiData, setApiData] = useState({
    cities: [],
    industries: [],
    programs: [],
    skills: [],
    organizations: []
  });

  const [filteredData, setFilteredData] = useState({
    cities: [],
    industries: [],
    programs: [],
    skills: [],
    organizations: []
  });

  const [dropdownOpen, setDropdownOpen] = useState({
    city: false,
    skills: false,
    education: false,
    organization: false,
    program: false,
    industry: false
  });

  const [isLoading, setIsLoading] = useState(false);

  const [loadingStates, setLoadingStates] = useState({
    cities: false,
    industries: false,
    programs: false,
    skills: false,
    organizations: false
  });
  
  const timeoutRefs = useRef({
    city: null,
    skills: null,
    organization: null,
    program: null,
    industry: null
  });
  const dropdownRefs = useRef({});

  // Get session data
  const InstituteId = sessionStorage.getItem("instituteId");
  const UserId = sessionStorage.getItem("id");
  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;

  // Static constants
  const employmentTypes = ['job', 'internship'];
  const degreeStatuses = ['Under Graduate', 'Graduate', 'Post Graduate'];
  const shiftTypes = ['Work from home', 'On site'];

  const DEBOUNCE_DELAYS = {
    city: 500,
    skills: 500,
    organization: 500,
    program: 500,
    industry: 500
  };

  // Get today's date in YYYY-MM-DD format for min attribute
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  const clearTimeouts = useCallback(() => {
    Object.keys(timeoutRefs.current).forEach(key => {
      if (timeoutRefs.current[key]) {
        clearTimeout(timeoutRefs.current[key]);
        timeoutRefs.current[key] = null;
      }
    });
  }, []);

  // Generic debounced search function
  const debouncedSearch = useCallback((searchType, searchTerm, delay) => {
    if (timeoutRefs.current[searchType]) {
      clearTimeout(timeoutRefs.current[searchType]);
    }

    setLoadingStates(prev => ({ ...prev, [searchType]: true }));

    timeoutRefs.current[searchType] = setTimeout(async () => {
      try {
        let results = [];
        switch (searchType) {
          case 'city':
            results = await fetchCities(searchTerm);
            setFilteredData(prev => ({ ...prev, cities: results }));
            break;
          case 'skills':
            results = await fetchSkills(searchTerm);
            setFilteredData(prev => ({ ...prev, skills: results }));
            break;
          case 'organization':
            results = await fetchOrganizations(searchTerm);
            setFilteredData(prev => ({ ...prev, organizations: results }));
            break;
          case 'program':
            results = await fetchPrograms(searchTerm);
            setFilteredData(prev => ({ ...prev, programs: results }));
            break;
          case 'industry':
            results = await fetchIndustries(searchTerm);
            setFilteredData(prev => ({ ...prev, industries: results }));
            break;
          default:
            break;
        }
      } catch (error) {
        console.error(`Error searching ${searchType}:`, error);
      } finally {
        setLoadingStates(prev => ({ ...prev, [searchType]: false }));
        timeoutRefs.current[searchType] = null;
      }
    }, delay);
  }, []);

  // API calling functions
  const fetchCities = async (searchTerm = '') => {
    try {
      const queryParams = new URLSearchParams({ searchTerm }).toString();
      const response = await fetch(`${LMS}/api/search-city?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      return data.cities || data.data || [];
    } catch (error) {
      console.error('Error fetching cities:', error);
      return [];
    }
  };

  const fetchIndustries = async (searchTerm = '') => {
    try {
      const queryParams = searchTerm ? new URLSearchParams({ searchTerm }).toString() : '';
      const response = await fetch(`${LMS}/api/get-industries${queryParams ? `?${queryParams}` : ''}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      const data = await response.json();
      return data.industries || data.data || [];
    } catch (error) {
      console.error('Error fetching industries:', error);
      return [];
    }
  };

  const fetchPrograms = async (searchTerm = '') => {
    try {
      const queryParams = new URLSearchParams({ 
        user_id: parseInt(UserId),
        ...(searchTerm && { searchTerm })
      }).toString();
      
      const response = await fetch(`${LMS}/api/get-institute-program?${queryParams}&institute_id=${InstituteId}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      const data = await response.json();
      return data.programs || data.data || [];
    } catch (error) {
      console.error('Error fetching programs:', error);
      return [];
    }
  };

  const fetchSkills = async (searchTerm = '') => {
    try {
      const queryParams = new URLSearchParams({ searchTerm }).toString();
      const response = await fetch(`${LMS}/api/skill?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      const data = await response.json();
      return data.skills || data.data || [];
    } catch (error) {
      console.error('Error fetching skills:', error);
      return [];
    }
  };

  const fetchOrganizations = async (searchTerm = '') => {
    try {
      const queryParams = new URLSearchParams({ 
        institute_id: parseInt(InstituteId),
        ...(searchTerm && { searchTerm })
      }).toString();

      const response = await fetch(`${LMS}/api/organisation?${queryParams}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      const data = await response.json();
      return data.organizations || data.data || [];
    } catch (error) {
      console.error('Error fetching organizations:', error);
      return [];
    }
  };

  const callDefaultAPI = useCallback(async () => {
    setIsLoading(true);
    try {
      const results = await Promise.allSettled([
        fetchSkills(''),
        fetchPrograms(''),
        fetchIndustries(''),
        fetchOrganizations(''),
      ]);

      const [skillsResult, programsResult, industriesResult, organizationsResult] = results;

      const newData = {
        skills: skillsResult.status === 'fulfilled' ? skillsResult.value : [],
        programs: programsResult.status === 'fulfilled' ? programsResult.value : [],
        industries: industriesResult.status === 'fulfilled' ? industriesResult.value : [],
        organizations: organizationsResult.status === 'fulfilled' ? organizationsResult.value : [],
      };

      results.forEach((result, index) => {
        const apiNames = ['skills', 'programs', 'industries', 'organizations'];
        if (result.status === 'rejected') {
          console.error(`Failed to load ${apiNames[index]}:`, result.reason);
        }
      });

      setApiData(newData);
      setFilteredData(newData);
    } catch (error) {
      console.error('Critical error loading API data:', error);
      const emptyData = {
        skills: [],
        programs: [],
        industries: [],
        organizations: []
      };
      setApiData(emptyData);
      setFilteredData(emptyData);
    } finally {
      setIsLoading(false);
    }
  }, [InstituteId, UserId]);

  useEffect(() => {
    if (InstituteId && UserId) {
      callDefaultAPI();
    }
  }, [InstituteId, UserId, callDefaultAPI]);

  const handleSearchChange = useCallback((field, value) => {
    setSearchTerms(prev => ({ ...prev, [field]: value }));
    setDropdownOpen(prev => ({ ...prev, [field]: true }));

    const fieldToSearchTypeMap = {
      city: 'city',
      skills: 'skills',
      organization: 'organization',
      program: 'program',
      industry: 'industry'
    };

    const searchType = fieldToSearchTypeMap[field];
    
    if (searchType && value.trim()) {
      const delay = DEBOUNCE_DELAYS[searchType];
      debouncedSearch(searchType, value.trim(), delay);
    } else if (searchType && !value.trim()) {
      if (timeoutRefs.current[searchType]) {
        clearTimeout(timeoutRefs.current[searchType]);
        timeoutRefs.current[searchType] = null;
      }
      setLoadingStates(prev => ({ ...prev, [searchType]: false }));
      
      const dataMap = {
        city: 'cities',
        skills: 'skills',
        organization: 'organizations',
        program: 'programs',
        industry: 'industries'
      };
      
      const dataKey = dataMap[searchType];
      if (dataKey) {
        setFilteredData(prev => ({ ...prev, [dataKey]: apiData[dataKey] }));
      }
    }
  }, [debouncedSearch, apiData]);

  useEffect(() => {
    return () => {
      clearTimeouts();
    };
  }, [clearTimeouts]);

  const formatDisplayName = (item) => {
    if (typeof item === 'string') return item;
    return (
      item.skill_title ||
      item.name ||
      item.title ||
      item.display_name ||
      item.organisation_name ||
      item.program_title ||
      String(item)
    );
  };

  const getItemValue = (item) => {
    if (typeof item === 'string') return item;
    return item.id || item.value || item.name || item.title || item.display_name || item.skill_title || item.organisation_name || item.program_title;
  };

  const getItemId = (item) => {
    if (typeof item === 'string') return item;
    if (item.id !== undefined && item.id !== null) return item.id;
    if (item.value !== undefined && item.value !== null) return item.value;
    return formatDisplayName(item);
  };

  // Handle multi-select for cities, skills, and education
  // const handleMultiSelect = (field, item) => {
  //   const currentValues = formData[field] || [];
  //   const itemId = getItemId(item);
  //   const itemName = formatDisplayName(item);
    
  //   const existingIndex = currentValues.findIndex(val => val.id === itemId);
    
  //   let newValues;
  //   if (existingIndex > -1) {
  //     newValues = currentValues.filter(val => val.id !== itemId);
  //   } else {
  //     newValues = [...currentValues, { id: itemId, name: itemName }];
  //   }
    
  //   if (onArrayFieldChange) {
  //     onArrayFieldChange(field, newValues);
  //   }
    
  //   setDropdownOpen(prev => ({ ...prev, [field]: false }));
  //   setSearchTerms(prev => ({ ...prev, [field]: '' }));
  // };

  const handleMultiSelect = (field, item) => {
    const currentValues = formData[field] || [];
    const itemId = getItemId(item);
    const itemName = formatDisplayName(item);

    const existingIndex = currentValues.findIndex(val => val.id === itemId);

    let newValues;
    if (existingIndex > -1) {
      newValues = currentValues.filter(val => val.id !== itemId);
    } else {
      newValues = [...currentValues, { id: itemId, name: itemName }];
    }

    if (onArrayFieldChange) {
      onArrayFieldChange(field, newValues);
    }
    setDropdownOpen(prev => ({ ...prev, [field]: true }));
    setSearchTerms(prev => ({ ...prev, [field]: '' }));
  };

  const handleSingleSelect = (field, item) => {
    const itemId = getItemId(item);
    const itemName = formatDisplayName(item);

    onInputChange({
      target: {
        name: field,
        value: itemId
      }
    });
    
    setDropdownOpen(prev => ({ ...prev, [field]: false }));
    setSearchTerms(prev => ({ ...prev, [field]: '' }));
    
    setTimeout(() => {
      const fieldKey = field === 'organisation_id' ? 'organization' : 
                        field === 'program_id' ? 'program' : 
                        field === 'industry_id' ? 'industry' : field;
      setDropdownOpen(prev => ({ ...prev, [fieldKey]: false }));
    }, 0);
  };

  const handleTaggedInputChange = (field, value) => {
    if (value.includes(',')) {
      const items = value.split(',').map(item => item.trim()).filter(item => item);
      const newItems = items.slice(0, -1);
      const lastItem = items[items.length - 1];
      
      if (newItems.length > 0 && field !== 'city_id') {
        const currentValues = formData[field] || [];
        const newItemsToAdd = newItems
          .filter(item => !currentValues.some(val => val.id === item || val.name === item))
          .map(item => ({ id: item, name: item }));
        
        if (newItemsToAdd.length > 0) {
          const newValues = [...currentValues, ...newItemsToAdd];
          
          if (onArrayFieldChange) {
            onArrayFieldChange(field, newValues);
          }
        }
      }
      
      setSearchTerms(prev => ({ ...prev, [field]: lastItem || '' }));
      
      if (field === 'city_id' && lastItem) {
        handleSearchChange('city', lastItem);
      }
    } else {
      setSearchTerms(prev => ({ ...prev, [field]: value }));
      if (field === 'city_id') {
        handleSearchChange('city', value);
      }
    }
  };

  const handleTaggedInputKeyDown = (field, event) => {
    if (event.key === 'Enter' || event.key === 'Tab') {
      const value = searchTerms[field]?.trim();
      if (value && field !== 'city_id') {
        event.preventDefault();
        const currentValues = formData[field] || [];
        
        const exists = currentValues.some(val => val.id === value || val.name === value);
        if (!exists) {
          const newItem = { id: value, name: value };
          const newValues = [...currentValues, newItem];
          
          if (onArrayFieldChange) {
            onArrayFieldChange(field, newValues);
          }
          
          setSearchTerms(prev => ({ ...prev, [field]: '' }));
        }
      }
    }
  };

  const handleTaggedInputBlur = (field) => {
    const related = document?.activeElement;
    if (related && dropdownRefs.current[field] && dropdownRefs.current[field].contains(related)) {
      return;
    }
    const value = searchTerms[field]?.trim();
    if (value && field !== 'city_id' && field !== 'education') {
      const currentValues = formData[field] || [];
      
      const exists = currentValues.some(val => String(val.id) === String(value) || String(val.name) === String(value));
      if (!exists) {
        const newItem = { id: value, name: value };
        const newValues = [...currentValues, newItem];
        
        if (onArrayFieldChange) {
          onArrayFieldChange(field, newValues);
        }
        setSearchTerms(prev => ({ ...prev, [field]: '' }));
      }
   }
  };

  const removeTag = (field, itemId) => {
    const currentValues = formData[field] || [];
    const newValues = currentValues.filter(val => val.id !== itemId);
    
    if (onArrayFieldChange) {
      onArrayFieldChange(field, newValues);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.dropdown-container')) {
        setDropdownOpen({
          city: false,
          skills: false,
          education: false,
          organization: false,
          program: false,
          industry: false
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const renderSearchableDropdown = (field, options, placeholder, fieldName, icon = null) => {
    const currentValue = formData[fieldName] || '';
    const selectedItem = options.find(item => getItemId(item) === currentValue);
    const displayValue = selectedItem ? formatDisplayName(selectedItem) : '';
    
    const handleClearSelection = () => {
      onInputChange({
        target: {
          name: fieldName,
          value: ''
        }
      });
      setSearchTerms(prev => ({ ...prev, [field]: '' }));
      setDropdownOpen(prev => ({ ...prev, [field]: false }));
    };
    const isSearchLoading = loadingStates[field] || false;
    const optionData = options?.filter(option => formatDisplayName(option).toLowerCase().includes(searchTerms[field].toLowerCase()))

    return (
      <div className="dropdown-container position-relative">
        <div className="input-group">
          <input
            type="text"
            className={`form-control ${errors[fieldName] ? 'is-invalid' : ''}`}
            value={dropdownOpen[field] ? searchTerms[field] : displayValue}
            onChange={(e) => handleSearchChange(field, e.target.value)}
            onFocus={() => {
              setDropdownOpen(prev => ({ ...prev, [field]: true }));
              setSearchTerms(prev => ({ ...prev, [field]: displayValue }));
            }}
            placeholder={placeholder}
          />
          {displayValue && !dropdownOpen[field] && (
            <span className="input-group-text bg-transparent border-0 position-absolute end-0 top-50 translate-middle-y me-2" 
                  style={{ cursor: 'pointer', zIndex: 10 }}>
              <X size={16} className="text-muted" onClick={handleClearSelection} />
            </span>
          )}
        </div>
        
        {dropdownOpen[field] && (
          <div className="dropdown-menu show w-100" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1050 }}>
            {isSearchLoading ? (
              <div className="dropdown-item-text d-flex align-items-center gap-2">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Loading...
              </div>
            ) : options.length === 0 ? (
              <div className="dropdown-item-text">No results found</div>
            ) : (
              optionData?.length == 0 ? <div className="dropdown-item-text">No results found</div>:
              optionData?.map((option, index) => {
                return(
                  <button
                    key={index}
                    type="button"
                    className="dropdown-item"
                    onClick={() => handleSingleSelect(fieldName, option)}
                  >
                    {formatDisplayName(option)}
                  </button>
                )
              })
            )
            }
          </div>
        )}
      </div>
    );
  };

  const renderTaggedInput = (field, options, placeholder, icon, enableDropdown = true) => {
    const currentValues = formData[field] || [];
    const searchValue = searchTerms[field] || '';
    const fieldKey = field === 'city_id' ? 'city' : field;
    const isSearchLoading = loadingStates[fieldKey] || false;
    const renderData = options?.filter(option => formatDisplayName(option).toLowerCase().includes(searchValue.toLowerCase()) &&
                  !currentValues.some(val => String(val.id) === String(getItemId(option))))
    return (
       <div className="dropdown-container position-relative" ref={(el) => (dropdownRefs.current[field] = el)}>
        <div className={`form-control ${errors[field] ? 'is-invalid' : ''}`}>
          <div className="d-flex flex-wrap gap-2">
            {currentValues.map((item, index) => (
              <span key={index} className="badge bg-primary d-flex align-items-center gap-1">
                {item.name}
                <X 
                  size={14} 
                  className="cursor-pointer" 
                  onClick={() => removeTag(field, item.id)}
                />
              </span>
            ))}
            <input
              type="text"
              className="border-0 outline-0 flex-grow-1"
              style={{ minWidth: '120px', outline: 'none' }}
              value={searchValue}
              // onChange={(e) => handleTaggedInputChange(field, e.target.value)}
              // onBlur={(e) => handleTaggedInputBlur(field, e)}
              // onChange={(e) => {
              //     const value = e.target.value;
              //     if (value.includes(",")) {
              //       const parts = value.split(",").map(v => v.trim()).filter(v => v !== "");
              //       parts.forEach((part) => handleMultiSelect(field, part));
              //     } else{
              //       handleTaggedInputChange(field, e.target.value)
              //     }
              // }}
              onChange={(e) => {
                const value = e.target.value;
                if (value.includes(",")) {
                  const parts = value.split(",").map(v => v.trim()).filter(v => v !== "");
                  parts.forEach((part) => handleMultiSelect(field, part));
                  setSearchTerms(prev => ({ ...prev, [field]: '' })); // reset search
                  setDropdownOpen(prev => ({ ...prev, [field]: true })); // reopen dropdown
                } else {
                  handleTaggedInputChange(field, value);
                  setDropdownOpen(prev => ({ ...prev, [field]: true })); // ensure dropdown opens while typing
                }
              }}
              onKeyDown={(e) => handleTaggedInputKeyDown(field, e)}
              onBlur={() => {
                  if (searchValue.trim() !== '') {
                    handleMultiSelect(field, searchValue.trim());
                  }
                }}
              onFocus={() => enableDropdown && setDropdownOpen(prev => ({ ...prev, [field]: true }))}
              placeholder={currentValues.length === 0 ? placeholder : ''}
            />
          </div>
        </div>
        {icon && <span className="position-absolute top-50 end-0 translate-middle-y me-3 text-muted">{icon}</span>}
        
        {enableDropdown && dropdownOpen[field] && (
          <div className="dropdown-menu show w-100" style={{ maxHeight: '200px', overflowY: 'auto', zIndex: 1050 }}>
            {isSearchLoading ? (
              <div className="dropdown-item-text d-flex align-items-center gap-2">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                Loading...
              </div>
            ) : 
            (!searchValue && field === 'city_id') ? ( <div className="dropdown-item-text">Type at least 3 characters to search</div>) :
            options.length === 0 ? 
              <div className="dropdown-item-text">No results found</div> 
             : (
              renderData?.length==0? <div className="dropdown-item-text">No results found</div>:
              renderData?.map((option) => {
                  const optKey = String(getItemId(option));
                  return (
                    <button
                      key={optKey}
                      type="button"
                      className="dropdown-item"
                      onMouseDown={(e) => { e.preventDefault(); handleMultiSelect(field, option); }}
                    >
                      {formatDisplayName(option)}
                    </button>
                  );
                })
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="row g-4">
      {/* Left Column */}
      <div className="col-lg-6">
        {/* Organization with Search */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <Building size={16} className="me-2" />
            Organization/Company *
          </label>
          {renderSearchableDropdown('organization', filteredData.organizations, 'Search and select organization...', 'organisation_id', <Search size={16} />)}
          {errors.organisation_id && <div className="invalid-feedback d-block">{errors.organisation_id}</div>}
        </div>

        {/* Employment Type */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <Briefcase size={16} className="me-2" />
            Employment Type *
          </label>
          <select
            className={`form-select ${errors.employment_type ? 'is-invalid' : ''}`}
            name="employment_type"
            value={formData.employment_type || ''}
            onChange={onInputChange}
          >
            <option value="">Select employment type</option>
            {employmentTypes.map(type => (
              <option key={type} value={type} className='text-capitalize'>{type}</option>
            ))}
          </select>
          {errors.employment_type && <div className="invalid-feedback">{errors.employment_type}</div>}
        </div>

        {/* Duration */}
        {formData.employment_type && (
          <div className="mb-4">
            <label className="form-label fw-semibold text-dark mb-2">
              <Clock size={16} className="me-2" />
              {formData.employment_type === 'job' ? 'Job Bond Duration' : 'Internship Duration'}
            </label>
            <input
              type="text"
              className={`form-control ${errors.duration ? 'is-invalid' : ''}`}
              name="duration"
              value={formData.duration || ''}
              onChange={onInputChange}
              placeholder={formData.employment_type === 'job' ? 'e.g., 2 years' : 'e.g., 6 months'}
            />
            {errors.duration && <div className="invalid-feedback">{errors.duration}</div>}
          </div>
        )}

        {/* City with Multi-Select */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <MapPin size={16} className="me-2" />
            Location *
          </label>
          {renderTaggedInput('city_id', filteredData.cities, 'Type to search and select cities...', <Search size={16} />)}
          {errors.city_id && <div className="invalid-feedback d-block">{errors.city_id}</div>}
        </div>

        {/* Openings */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <Users size={16} className="me-2" />
            Number of Openings *
          </label>
          <input
            type="number"
            className={`form-control ${errors.openings ? 'is-invalid' : ''}`}
            name="openings"
            value={formData.openings || ''}
            onChange={onInputChange}
            placeholder="e.g., 5"
            min="1"
          />
          {errors.openings && <div className="invalid-feedback">{errors.openings}</div>}
        </div>

        {/* Education with Multi-Select */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <GraduationCap size={16} className="me-2" />
            Education Requirements *
          </label>
          {renderTaggedInput('education', [], 'Type education requirements (e.g., BE, ME, PhD)...', null, false)}
          {errors.education && <div className="invalid-feedback d-block">{errors.education}</div>}
        </div>

        {/* Industry with Search */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <Building size={16} className="me-2" />
            Industry *
          </label>
          {renderSearchableDropdown('industry', filteredData.industries, 'Search and select industry...', 'industry_id', <Search size={16} />)}
          {errors.industry_id && <div className="invalid-feedback d-block">{errors.industry_id}</div>}
        </div>

        {/* Expiry Date */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <Calendar size={16} className="me-2" />
            Expiry Date *
          </label>
          <input
            type="date"
            className={`form-control ${errors.expiry_date ? 'is-invalid' : ''}`}
            name="expiry_date"
            value={formData.expiry_date || ''}
            onChange={onInputChange}
            onClick={(e) => {
                  e.target.type = "date";
                  e.target.showPicker?.()
                }}
            min={getTodayDate()}
          />
          {errors.expiry_date && <div className="invalid-feedback">{errors.expiry_date}</div>}
        </div>
      </div>

      {/* Right Column */}
      <div className="col-lg-6">
        {/* Employment Title */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <Briefcase size={16} className="me-2" />
            Employment Title *
          </label>
          <input
            type="text"
            className={`form-control ${errors.employment_title ? 'is-invalid' : ''}`}
            name="employment_title"
            value={formData.employment_title || ''}
            onChange={onInputChange}
            placeholder="e.g., Senior Software Engineer"
          />
          {errors.employment_title && <div className="invalid-feedback">{errors.employment_title}</div>}
        </div>

        {/* Degree Status */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <Award size={16} className="me-2" />
            Degree Status *
          </label>
          <select
            className={`form-select ${errors.degree_status ? 'is-invalid' : ''}`}
            name="degree_status"
            value={formData.degree_status || ''}
            onChange={onInputChange}
          >
            <option value="">Select degree status</option>
            {degreeStatuses.map(status => (
              <option key={status} value={status}>{status}</option>
            ))}
          </select>
          {errors.degree_status && <div className="invalid-feedback">{errors.degree_status}</div>}
        </div>

        {/* Program with Search */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <FileText size={16} className="me-2" />
            Program
          </label>
          {renderSearchableDropdown('program', filteredData.programs, 'Search and select program...', 'program_id', <Search size={16} />)}
          {errors.program_id && <div className="invalid-feedback d-block">{errors.program_id}</div>}
        </div>

        {/* Salary */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <span className="me-2">₹</span>
            Salary / Stipend <span className="text-muted fw-normal">(per annum / per month)</span>
          </label>
          <input
            type="text"
            className={`form-control ${errors.salary ? 'is-invalid' : ''}`}
            name="salary"
            value={formData.salary || ''}
            onChange={onInputChange}
            placeholder="e.g., ₹8,00,000"
          />
          {errors.salary && <div className="invalid-feedback">{errors.salary}</div>}
        </div>

        {/* Skills with Multi-Select */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <Award size={16} className="me-2" />
            Required Skills *
          </label>
          {renderTaggedInput('skills', filteredData.skills, 'Type to search and select skills...', <Search size={16} />)}
          {errors.skills && <div className="invalid-feedback d-block">{errors.skills}</div>}
        </div>

        {/* Shift Type */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">
            <Clock size={16} className="me-2" />
            Work Type *
          </label>
          <select
            className={`form-select ${errors.type_of_shift ? 'is-invalid' : ''}`}
            name="type_of_shift"
            value={formData.type_of_shift || ''}
            onChange={onInputChange}
          >
            <option value="">Select shift type</option>
            {shiftTypes.map(shift => (
              <option key={shift} value={shift}>{shift}</option>
            ))}
          </select>
          {errors.type_of_shift && <div className="invalid-feedback">{errors.type_of_shift}</div>}
        </div>

        {/* Status */}
        <div className="mb-4">
          <label className="form-label fw-semibold text-dark mb-2">Status *</label>
          <div className="d-flex gap-4">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="status"
                value="1"
                checked={formData.status === '1'}
                onChange={onInputChange}
                id="status-active"
              />
              <label className="form-check-label" htmlFor="status-active">
                Active
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                name="status"
                value="0"
                checked={formData.status === '0'}
                onChange={onInputChange}
                id="status-inactive"
              />
              <label className="form-check-label" htmlFor="status-inactive">
                Inactive
              </label>
            </div>
          </div>
          {errors.status && <div className="invalid-feedback d-block">{errors.status}</div>}
        </div>
      </div>
    </div>
  );
};

export default JobInfoSection;
